import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { GateState } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Mascot } from '../components/Mascot'
import { WordPlantScene } from '../components/WordPlantScene'
import { LandingIconSvg, type LandingIcon } from '../components/LandingIcons'
import { AppPeek } from '../components/AppPeek'
import { useSignIn } from './SignIn'
import { DeviceCodeEntry } from '../components/DeviceCode'
import { useViewport, NARROW } from '../lib/screen'
import {
  SCREEN_LANGUAGES, rememberScreenLanguage, screenLanguage, setScreenLanguage, t, type ScreenLanguage,
} from '../lib/i18n'

/**
 * The front page: what anyone sees at wordsprout.net before signing in.
 *
 * **Redesigned 8 Oct 2026, from Sam's notes:** "too wordy", "three levels of
 * text, cut it to two", "we are using 80% of the screen, as if packing a lot
 * into a tight space; use 60%", "leave enough room between the title and How
 * it works", "keep Try it free visible without blocking the text".
 *
 *   * **Two levels of text.** The name, then two short phrases. No paragraph:
 *     How it works explains, for a parent who wants more.
 *   * **About 60% of a wide screen**, everything in one centred column, and
 *     the first part fills the first screen so How it works starts below it.
 *   * **On a phone, one part per screen**, and a swipe moves to the next
 *     (gentle scroll snapping, which never fights a deliberate scroll).
 *   * **Try it free never covers anything.** Once the big button scrolls out
 *     of sight, a small one appears in the top bar beside Sign in. The bar
 *     is its own strip, like a website menu, so no text ever sits under a
 *     floating button.
 *
 * The picture is Sprout Buddy watering letters into a word plant, which then
 * grows (WordPlantScene). Every claim on the page is true of the app as
 * built: one line from the research is deliberately NOT here, a "dataset of
 * 44,000 word meanings", because that dataset's licence forbids commercial
 * use and it was never downloaded.
 *
 * **One big button, whichever state sign-up is in.** While sign-up is closed
 * (the default until payment is switched on) "Try it free" says so honestly
 * and offers sign-in to families who already have an account.
 */
const FEATURES: [LandingIcon, string][] = [
  ['words', 'Their own words'],
  ['again', 'Back again until it sticks'],
  ['grows', 'Grows only with real learning'],
  ['report', 'Honest reports for you'],
]

// The two lines under the name. The second moved up from the page's foot
// and was cut from "Made by a parent, for children who love finding new
// words" (Sam: "more concise and punchier").
const LINE_ONE = 'Growing one word at a time. A few minutes a day.'
const LINE_TWO = 'Made by a parent, for word-loving kids.'

// Three text sizes on the whole page (design idea 4, 8 Oct): the name, one
// medium size for phrases, headings, cards and buttons, and one small size
// for chips and small print. Fewer sizes reads calmer.
const M = 20
const S = 15

const PROMISES = ['No ads', 'No streaks to keep', 'Nothing to buy inside', 'Answers stay private']

// A Chinese character is about twice as wide as an English letter (a little
// more, for room at the edges of a phone), so the two
// one-line phrases are sized by width, not by how many characters they have.
const ems = (text: string) => [...text].reduce((n, c) => n + ((c.codePointAt(0) ?? 0) >= 0x2e80 ? 1.1 : 0.5), 0)

export function Landing({ gate, onDone, onTry }: {
  gate: GateState | null; onDone: () => void
  /** The trial kept on this device (TryIt.tsx), when it is switched on. */
  onTry?: () => void
}) {
  const { go, busy, problem } = useSignIn(gate, onDone)
  const [closedNote, setClosedNote] = useState(false)
  const [deviceEntry, setDeviceEntry] = useState(false)
  const [heroButtonGone, setHeroButtonGone] = useState(false)
  const heroButton = useRef<HTMLButtonElement | null>(null)
  const { width, height } = useViewport()
  const narrow = width < NARROW
  const signupOpen = Boolean(gate?.signup_open)
  // Picked here, before sign-in, and kept in this browser (round 14).
  const [, setLanguage] = useState<ScreenLanguage>(screenLanguage())
  const pick = (lang: ScreenLanguage) => {
    setScreenLanguage(lang)
    rememberScreenLanguage(lang)
    setLanguage(lang)
  }
  const lineOne = t(LINE_ONE)
  const lineTwo = t(LINE_TWO)

  const tryIt = () => {
    if (signupOpen) { void go(); return }
    setClosedNote(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // The small top-bar button appears only once the big one is out of sight,
  // so there are never two "Try it free" buttons on screen at once.
  useEffect(() => {
    const el = heroButton.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    // The top 56 pixels are the bar itself, so under the bar counts as gone.
    const watch = new IntersectionObserver(
      ([entry]) => setHeroButtonGone(!entry?.isIntersecting),
      { threshold: 0.2 })
    watch.observe(el)
    return () => watch.disconnect()
  }, [])

  // On a phone, each part is a screen of its own and a swipe settles on the
  // next one. "proximity", not "mandatory": a long deliberate scroll is never
  // pulled back.
  useEffect(() => {
    const root = document.documentElement
    const before = root.style.scrollSnapType
    root.style.scrollSnapType = narrow ? 'y proximity' : ''
    return () => { root.style.scrollSnapType = before }
  }, [narrow])

  const column: CSSProperties = {
    width: 'min(calc(100% - 32px), max(560px, 60vw), 820px)',
    margin: '0 auto',
  }
  // The first screen must fit without scrolling, in any window (Sam, 8 Oct:
  // in a short laptop window the title was below the fold). So it is sized
  // from the window's height: the text first, and the picture takes what
  // room is left, between 110 and 320 pixels tall.
  const colW = narrow ? width - 32 : Math.min(Math.max(560, width * 0.6), 820, width - 32)
  // No top bar on the first screen any more (see the bar below), so all of
  // the window is the first screen's, less a little breathing room and the
  // "Already have an account?" line under the button.
  const room = height - 28 - 30
  const clamp = (lo: number, v: number, hi: number) => Math.max(lo, Math.min(hi, v))
  const titleSize = clamp(30, room * 0.1, narrow ? 44 : 58)
  // M and S, shrunk only when the window is too short or narrow for them.
  // A little bigger than the medium size (Sam, 8 Oct: "can be a bit bigger").
  const phraseSize = Math.min(clamp(14, room * 0.036, 24), colW / ems(lineOne))
  const smallSize = Math.min(clamp(12, room * 0.026, S), colW / (ems(lineTwo) * 0.96), phraseSize * 0.9)
  const compact = room < 560
  const buttonH = compact ? 48 : 60
  const buttonGap = compact ? 16 : 26
  const textH = titleSize * 1.08 + phraseSize * 1.4 + smallSize * 1.4 + 16 + buttonGap + buttonH + 8
    + (signupOpen ? 30 : 0)
    + 28 // "Setting up a child's tablet?"
    + 40 // the language choice
  const sceneH = clamp(110, room - textH - 12, 320)
  const sceneW = Math.min(sceneH * 360 / 260, colW, 440)

  const panel: CSSProperties = narrow
    ? { minHeight: '100svh', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 0' }
    : {}

  return (
    <div style={{
      minHeight: '100dvh',
      background: `radial-gradient(1200px 600px at 50% -10%, ${theme.greenPale}, ${theme.bg} 60%)`,
      paddingBottom: 40,
    }}>
      {/* ---------------------------------------------------- top bar
          Not there on the first screen (Sam, 8 Oct: the corner logo
          repeated the big name below it, and Sign in alone on the right
          looked lopsided). Sign in sits under Try it free instead. Once the
          big button scrolls away, this bar slides in: logo on the left,
          Sign in and Try it free on the right, balanced. */}
      <nav aria-hidden={!heroButtonGone} style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20, height: 56,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: theme.bg, borderBottom: `1px solid ${theme.borderSoft}`,
        boxShadow: '0 2px 10px rgba(21,32,43,0.06)',
        transform: heroButtonGone ? 'none' : 'translateY(-110%)',
        transition: 'transform 0.25s ease',
      }}>
        <div style={{ ...column, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mascot size={30} animate={false} label="" />
            <span style={{
              fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: M,
              color: theme.greenDarker,
            }}>Word Sprout</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={() => void go()} disabled={busy}
              tabIndex={heroButtonGone ? 0 : -1} style={{
              background: 'transparent', border: 'none', color: theme.blue,
              fontWeight: 700, fontSize: S, cursor: 'pointer', minHeight: TAP_MIN,
              padding: '0 4px', fontFamily: 'inherit',
            }}>{t('Sign in')}</button>
            <button type="button" onClick={tryIt} disabled={busy}
              tabIndex={heroButtonGone ? 0 : -1}
              style={{
                fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: S,
                color: '#fff', background: theme.orange, border: 'none',
                borderRadius: radius.pill, padding: '0 16px', height: 38,
                cursor: 'pointer', boxShadow: `0 3px 0 ${theme.orangeDark}`,
              }}>{t('Try it free')}</button>
          </div>
        </div>
      </nav>

      {/* ------------------------------------------------------- hero */}
      <section style={{
        ...column, ...panel, textAlign: 'center',
        minHeight: narrow ? panel.minHeight : '100svh',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      }}>
        <WordPlantScene width={Math.round(sceneW)} />
        <h1 style={{
          margin: '10px 0 0', fontSize: titleSize, lineHeight: 1.05,
          color: theme.greenDarker,
        }}>
          Word Sprout
        </h1>
        {/* Two lines, each on one line (Sam): what it is, then who made it. */}
        <p style={{
          margin: '8px 0 0', fontFamily: "'Fredoka', sans-serif", whiteSpace: 'nowrap',
          // Green, not orange (design idea 1, 8 Oct): orange is kept for the
          // buttons alone, so Try it free is the one orange thing to look at.
          fontSize: phraseSize, fontWeight: 500, color: theme.greenDark,
        }}>{lineOne}</p>
        <p style={{
          margin: '6px 0 0', whiteSpace: 'nowrap',
          fontSize: smallSize, fontWeight: 600, color: theme.muted,
        }}>{lineTwo}</p>
        <button
          ref={heroButton}
          type="button"
          onClick={tryIt}
          disabled={busy}
          style={{
            marginTop: buttonGap,
            fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: M,
            color: '#fff', background: theme.orange, border: 'none',
            borderRadius: radius.pill, padding: compact ? '10px 34px' : '16px 44px', minHeight: buttonH,
            cursor: busy ? 'wait' : 'pointer',
            boxShadow: `0 6px 0 ${theme.orangeDark}, 0 10px 24px rgba(232,131,58,0.35)`,
          }}
        >
          {busy ? t('One moment…') : t('Try it free')}
        </button>
        <p style={{ margin: '14px 0 0', fontSize: S, color: theme.muted }}>
          {t('Already have an account?')}{' '}
          <button type="button" onClick={() => void go()} disabled={busy} style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: theme.blue, fontWeight: 800, fontSize: S, fontFamily: 'inherit',
          }}>{t('Sign in')}</button>
        </p>
        {/* A child's tablet signs in with a code from a grown-up's phone,
            so Google never touches it (22 Oct 2026, components/DeviceCode). */}
        <p style={{ margin: '8px 0 0', fontSize: S, color: theme.muted }}>
          {t("Setting up a child's tablet?")}{' '}
          <button type="button" onClick={() => setDeviceEntry(true)} disabled={busy} style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: theme.blue, fontWeight: 800, fontSize: S, fontFamily: 'inherit',
          }}>{t('Use a device code')}</button>
        </p>
        {deviceEntry ? (
          <DeviceCodeEntry onDone={onDone} onCancel={() => setDeviceEntry(false)} />
        ) : null}
        {/* No account, nothing sent: the trial is kept in this browser (29 Oct 2026). */}
        {onTry ? (
          <p style={{ margin: '8px 0 0', fontSize: S, color: theme.muted }}>
            {t('Want to see it first?')}{' '}
            <button type="button" onClick={onTry} disabled={busy} style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              color: theme.blue, fontWeight: 800, fontSize: S, fontFamily: 'inherit',
            }}>{t('Try it on this device')}</button>
          </p>
        ) : null}
        <LanguageChoice onPick={pick} />
        {signupOpen ? (
          <p style={{ margin: '14px 0 0', fontSize: S, color: theme.muted }}>
            {t('Free for two weeks. Cancel any time.')}
          </p>
        ) : null}
        {problem ? (
          <p style={{ margin: '14px 0 0', color: theme.red, fontSize: S }}>{problem}</p>
        ) : null}
        {closedNote && !signupOpen ? (
          <div role="status" style={{
            marginTop: 16, background: theme.card, border: `1px solid ${theme.borderWarm}`,
            borderRadius: radius.lg, padding: 16, maxWidth: 420, textAlign: 'left',
          }}>
            <strong style={{ display: 'block', marginBottom: 4, color: theme.text }}>
              {t('We are not taking new families just yet.')}
            </strong>
            <span style={{ color: theme.text2, fontSize: S, lineHeight: 1.5 }}>
              {t('Word Sprout is being tested with a small group first. If your family already has an account, sign in here.')}
            </span>
            <div style={{ marginTop: 12 }}>
              <button type="button" onClick={() => void go()} disabled={busy} style={{
                background: theme.blue, color: '#fff', border: 'none', fontWeight: 700,
                fontSize: S, borderRadius: radius.md, padding: '10px 18px',
                minHeight: TAP_MIN, cursor: 'pointer',
              }}>{t('Sign in with Google')}</button>
            </div>
          </div>
        ) : null}
      </section>

      {/* ----------------------------------------------- how it works */}
      <section style={{ ...column, ...panel, paddingTop: narrow ? 24 : 72 }}>
        <h2 style={{ fontSize: M, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.greenDark, margin: '0 0 22px', textAlign: 'center' }}>
          {t('How it works')}
        </h2>
        {/* A peek inside the app beside the four cards (design idea 3):
            one picture of a real kind of question says more than words. */}
        <div style={{
          display: 'flex', gap: narrow ? 20 : 28, alignItems: 'center',
          flexDirection: narrow ? 'column' : 'row',
        }}>
          <AppPeek width={narrow ? 210 : 230} />
          {/* On a phone, two by two and compact, so the peek and all four
              fit on one screen. */}
          <div style={{
            flex: 1, width: '100%', display: 'grid',
            gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: narrow ? 10 : 12,
          }}>
            {FEATURES.map(([icon, title]) => (
              <Feature key={title} icon={icon} compact={narrow}>{t(title)}</Feature>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- promises */}
      <section style={{ ...column, ...panel, paddingTop: narrow ? 24 : 64, textAlign: 'center' }}>
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10,
          marginBottom: 36,
        }}>
          {PROMISES.map((p) => (
            <span key={p} style={{
              background: theme.card, border: `1px solid ${theme.borderSoft}`,
              borderRadius: radius.pill, padding: '9px 16px', fontSize: S,
              fontWeight: 700, color: theme.greenDark,
            }}>✓ {t(p)}</span>
          ))}
        </div>
        <div>
          <button type="button" onClick={tryIt} disabled={busy} style={{
            fontFamily: "'Fredoka', sans-serif", fontWeight: 600, fontSize: M,
            color: '#fff', background: theme.orange, border: 'none',
            borderRadius: radius.pill, padding: '13px 32px', minHeight: 52,
            cursor: 'pointer', boxShadow: `0 5px 0 ${theme.orangeDark}`,
          }}>{t('Try it free')}</button>
        </div>
      </section>
    </div>
  )
}

function Feature({
  icon, children, compact = false,
}: { icon: LandingIcon; children: ReactNode; compact?: boolean }) {
  return (
    <div style={{
      background: theme.card, border: `1px solid ${theme.borderWarm}`,
      borderRadius: radius.xl, padding: compact ? '12px 10px' : '20px 22px', display: 'flex',
      gap: compact ? 8 : 16, flexDirection: compact ? 'column' : 'row', textAlign: compact ? 'center' : 'left',
      alignItems: 'center', boxShadow: '0 2px 0 rgba(21,32,43,0.04)',
    }}>
      <div style={{
        width: 54, height: 54, flexShrink: 0, display: 'grid',
        placeItems: 'center', background: theme.greenPale, borderRadius: radius.lg,
      }}><LandingIconSvg name={icon} size={40} /></div>
      <h3 style={{ margin: 0, fontSize: compact ? S : M, color: theme.text, lineHeight: 1.25 }}>{children}</h3>
    </div>
  )
}

/**
 * English, 中文, Español: each written in its own language, so a parent who
 * reads no English can still find theirs. Only the grown-up screens change;
 * the children's practice stays English.
 */
function LanguageChoice({ onPick }: { onPick: (lang: ScreenLanguage) => void }) {
  const now = screenLanguage()
  const choices: { code: ScreenLanguage; label: string }[] = [{ code: 'en', label: 'English' }, ...SCREEN_LANGUAGES]
  return (
    <div role="group" aria-label="Language · 語言 · Idioma" style={{
      marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center',
    }}>
      {choices.map(({ code, label }) => (
        <button key={code} type="button" onClick={() => onPick(code)} aria-pressed={now === code} style={{
          background: now === code ? theme.blueFaint : 'transparent',
          border: `1px solid ${now === code ? theme.blueTint : 'transparent'}`,
          borderRadius: radius.pill, padding: '4px 12px', minHeight: 32, cursor: 'pointer',
          color: now === code ? theme.blue : theme.muted, fontWeight: now === code ? 800 : 600,
          fontSize: S, fontFamily: 'inherit',
        }}>{label}</button>
      ))}
    </div>
  )
}
